import { useCallback, useEffect, useRef, useState } from 'react';
import { extensionForMime, getVoiceEnvironment, getVoiceFeatures, VOICE_MIME_CANDIDATES } from '@/utils/voiceDiagnostics';
import { calculateAdaptiveTurnDecision, classifyTranscriptCompletion, createAdaptiveTurnTracker } from '@/voice/adaptiveTurnDetector';

const EMPTY_DIAGNOSTICS = {
  permission: 'unknown', streamActive: false, trackReadyState: 'none', trackEnabled: false, trackMuted: false,
  recorderState: 'inactive', audioContextState: 'none', rms: 0, peak: 0, fftEnergy: 0, blobSize: 0,
  blobDuration: 0, uploadStatus: 'idle', sttStatus: 'idle', codec: 'unselected', sampleRate: 0,
  recoveryAttempts: 0, lastError: '', lastErrorCode: '', microphoneStatus: 'idle', activeStreams: 0,
  turnConfidence: 0, adaptiveSilenceMs: 0, pauseCount: 0, semanticStatus: 'unknown', turnDecision: 'listening', speakingRateWpm: 0,
};

const classifyMicrophoneError = (error) => {
  const details = { name: error?.name || 'UnknownError', constraint: error?.constraint || null, rawMessage: error?.message || '' };
  const messages = {
    NotAllowedError: 'Microphone access was denied by the browser or operating system.',
    NotFoundError: 'No microphone input device is available.',
    NotReadableError: 'The microphone device could not be read. The browser or operating system may still be releasing it.',
    OverconstrainedError: `The microphone does not support the requested ${error?.constraint || 'audio'} constraint.`,
    AbortError: 'The browser aborted microphone initialization before a stream was created.',
    SecurityError: 'The browser security policy blocked microphone capture.',
    TypeError: 'This browser rejected the microphone request configuration.',
  };
  return { ...details, message: messages[details.name] || `Microphone initialization failed (${details.name}): ${details.rawMessage || 'unknown browser error'}.` };
};

export const useTurnRecorder = ({ apiBase, token, onTranscript, onError, onStage, isTurnCurrent }) => {
  const [diagnostics, setDiagnostics] = useState(EMPTY_DIAGNOSTICS);
  const [events, setEvents] = useState([]);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const streamRef = useRef(null);
  const recorderStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const contextRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const frameRef = useRef(null);
  const timersRef = useRef(new Set());
  const abortRef = useRef(null);
  const chunksRef = useRef([]);
  const processRef = useRef(false);
  const turnRef = useRef(null);
  const startedAtRef = useRef(0);
  const voiceDetectedRef = useRef(false);
  const micSignalRef = useRef(false);
  const lastVoiceAtRef = useRef(0);
  const voiceStartedAtRef = useRef(0);
  const adaptiveTrackerRef = useRef(createAdaptiveTurnTracker());
  const pauseHistoryRef = useRef([]);
  const probeAbortRef = useRef(null);
  const probeTranscriptRef = useRef(null);
  const lastProbedRevisionRef = useRef(-1);
  const speakerProfileRef = useRef({ speakingRateWpm: null, samples: 0 });
  const callbacksRef = useRef({ onTranscript, onError, onStage, isTurnCurrent });
  const tokenRef = useRef(token);
  const environmentRef = useRef(getVoiceEnvironment());
  const featuresRef = useRef(getVoiceFeatures());

  useEffect(() => { callbacksRef.current = { onTranscript, onError, onStage, isTurnCurrent }; }, [isTurnCurrent, onError, onStage, onTranscript]);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const update = useCallback((changes) => setDiagnostics((current) => ({ ...current, ...changes })), []);
  const emit = useCallback((stage, status, detail = {}) => {
    const clientMonotonicMs = performance.now();
    const event = { timestamp: new Date().toISOString(), clientMonotonicMs, stage, status, turnId: turnRef.current?.turnId, ...environmentRef.current, ...detail };
    console.info('VOICE EVENT', event);
    setEvents((current) => [...current.slice(-119), event]);
    callbacksRef.current.onStage?.(stage, { status, clientMonotonicMs, ...detail, turn: turnRef.current });
    return event;
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  const releaseResources = useCallback(async ({ stopRecorder = true } = {}) => {
    clearTimers();
    abortRef.current?.abort();
    abortRef.current = null;
    probeAbortRef.current?.abort();
    probeAbortRef.current = null;
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onerror = null;
      recorder.onstop = null;
      if (stopRecorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch (_) { /* already stopped */ }
      }
    }
    recorderRef.current = null;
    const tracks = new Set([
      ...(streamRef.current?.getTracks?.() || []),
      ...(recorderStreamRef.current?.getTracks?.() || []),
    ]);
    tracks.forEach((track) => {
      track.onended = null; track.onmute = null; track.onunmute = null;
      if (track.readyState !== 'ended') track.stop();
    });
    streamRef.current = null;
    recorderStreamRef.current = null;
    try { sourceRef.current?.disconnect(); } catch (_) { /* disconnected */ }
    sourceRef.current = null;
    analyserRef.current = null;
    if (contextRef.current && contextRef.current.state !== 'closed') {
      try { await contextRef.current.close(); } catch (_) { /* closing */ }
    }
    contextRef.current = null;
    setIsRecording(false);
    setAudioLevel(0);
    update({ streamActive: false, activeStreams: 0, trackReadyState: 'ended', recorderState: 'inactive', audioContextState: 'closed', microphoneStatus: 'released' });
    emit('RESOURCES_RELEASED', 'complete', { tracksStopped: tracks.size });
  }, [clearTimers, emit, update]);

  const reportError = useCallback((code, error, detail = {}) => {
    const message = typeof error === 'string' ? error : error.message;
    const affectedTurn = turnRef.current;
    if (!affectedTurn || !callbacksRef.current.isTurnCurrent?.(affectedTurn.turnId)) {
      emit(code, 'stale_ignored', { error: message, ...detail });
      return;
    }
    update({ lastError: message, lastErrorCode: code, microphoneStatus: 'error' });
    emit(code, 'error', { error: message, ...detail });
    callbacksRef.current.onError?.(message, code, { ...detail, turn: affectedTurn });
  }, [emit, update]);

  const setupAnalysis = useCallback(async (stream) => {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;
    contextRef.current = new AudioContextCtor();
    if (contextRef.current.state === 'suspended') await contextRef.current.resume().catch(() => {});
    analyserRef.current = contextRef.current.createAnalyser();
    analyserRef.current.fftSize = 2048;
    analyserRef.current.smoothingTimeConstant = 0.2;
    sourceRef.current = contextRef.current.createMediaStreamSource(stream);
    sourceRef.current.connect(analyserRef.current);
    update({ audioContextState: contextRef.current.state, sampleRate: contextRef.current.sampleRate });
  }, [update]);

  const acquireMicrophone = useCallback(async (turn) => {
    await releaseResources();
    if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error('getUserMedia is unavailable.'), { name: 'NotSupportedError' });
    const supported = navigator.mediaDevices.getSupportedConstraints?.() || {};
    const enhanced = {};
    if (supported.echoCancellation) enhanced.echoCancellation = { ideal: true };
    if (supported.noiseSuppression) enhanced.noiseSuppression = { ideal: true };
    if (supported.autoGainControl) enhanced.autoGainControl = { ideal: true };
    if (supported.channelCount) enhanced.channelCount = { ideal: 1 };
    const attempts = Object.keys(enhanced).length ? [{ audio: enhanced }, { audio: true }] : [{ audio: true }];
    let lastError;
    emit('MICROPHONE_REQUESTED', 'started', { attemptCount: attempts.length });
    for (let index = 0; index < attempts.length; index += 1) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(attempts[index]);
        const track = stream.getAudioTracks()[0];
        if (!track || track.readyState !== 'live' || !stream.active) throw Object.assign(new Error('The returned microphone track is inactive.'), { name: 'TrackInactiveError' });
        streamRef.current = stream;
        recorderStreamRef.current = new MediaStream([track]);
        track.onended = () => reportError('TRACK_ENDED', 'The microphone track ended unexpectedly.', { readyState: track.readyState });
        track.onmute = () => { update({ trackMuted: true }); emit('MICROPHONE_TRACK', 'muted'); };
        track.onunmute = () => { update({ trackMuted: false }); emit('MICROPHONE_TRACK', 'unmuted'); };
        if (index > 0 && track.applyConstraints) {
          for (const constraint of ['echoCancellation', 'noiseSuppression', 'autoGainControl']) {
            if (!supported[constraint]) continue;
            try { await track.applyConstraints({ [constraint]: true }); emit('OPTIONAL_CONSTRAINT', 'enabled', { constraint }); }
            catch (constraintError) { emit('OPTIONAL_CONSTRAINT', 'skipped', { constraint, error: constraintError.message }); }
          }
        }
        await setupAnalysis(stream);
        if (!callbacksRef.current.isTurnCurrent?.(turn.turnId)) {
          await releaseResources();
          return null;
        }
        update({ permission: 'granted', streamActive: true, activeStreams: 1, trackReadyState: track.readyState, trackEnabled: track.enabled, trackMuted: track.muted, microphoneStatus: 'ready', lastError: '', lastErrorCode: '' });
        emit('MICROPHONE_GRANTED', 'complete', { attempt: index + 1, settings: track.getSettings?.() || {} });
        return stream;
      } catch (error) {
        lastError = error;
        const classified = classifyMicrophoneError(error);
        emit('MICROPHONE_ATTEMPT', 'failed', { attempt: index + 1, ...classified });
        await releaseResources();
      }
    }
    const classified = classifyMicrophoneError(lastError);
    reportError('MICROPHONE_INITIALIZATION_FAILED', classified.message, classified);
    throw Object.assign(new Error(classified.message), classified);
  }, [emit, releaseResources, reportError, setupAnalysis, update]);

  const createRecorder = useCallback((stream) => {
    const supportedTypes = VOICE_MIME_CANDIDATES.filter((type) => window.MediaRecorder?.isTypeSupported?.(type));
    const candidates = [...supportedTypes, ''];
    let lastError;
    for (const mimeType of candidates) {
      try { return mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream); }
      catch (error) { lastError = error; emit('RECORDER_CODEC', 'failed', { mimeType: mimeType || 'browser-default', error: error.message }); }
    }
    throw lastError || new Error('No supported MediaRecorder codec could be initialized.');
  }, [emit]);

  const requestSemanticProbe = useCallback(async ({ blob, durationMs, mimeType, turn, speechRevision }) => {
    if (blob.size < 512 || !callbacksRef.current.isTurnCurrent?.(turn.turnId)) return;
    probeAbortRef.current?.abort();
    const controller = new AbortController();
    probeAbortRef.current = controller;
    adaptiveTrackerRef.current.setProbePending(true);
    emit('TURN_SEMANTIC_PROBE', 'started', { bytes: blob.size, durationMs, speechRevision });
    const requestId = window.crypto?.randomUUID?.() || `probe-${Date.now()}`;
    const form = new FormData();
    form.append('file', blob, `probe-${requestId}.${extensionForMime(mimeType)}`);
    form.append('request_id', requestId);
    form.append('duration_ms', String(Math.max(100, Math.min(35000, durationMs))));
    form.append('client_mime', mimeType);
    try {
      const response = await fetch(`${apiBase}/ai/transcribe/stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenRef.current}` },
        body: form,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Semantic probe failed with HTTP ${response.status}.`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let transcript = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const raw = block.split('\n').filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trim()).join('\n');
          if (raw) {
            const event = JSON.parse(raw);
            if (event.event === 'stt_complete') transcript = event.transcript?.trim() || '';
            if (event.event === 'error') throw new Error(event.error || 'Semantic probe failed.');
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
      const snapshot = adaptiveTrackerRef.current.getSnapshot();
      if (controller.signal.aborted || snapshot.speechRevision !== speechRevision || !callbacksRef.current.isTurnCurrent?.(turn.turnId)) return;
      const hint = classifyTranscriptCompletion(transcript);
      const words = transcript.split(/\s+/).filter(Boolean).length;
      const speakingRateWpm = words ? Math.round(words / Math.max(durationMs / 60000, 0.01)) : null;
      if (speakingRateWpm && speakingRateWpm >= 45 && speakingRateWpm <= 260) {
        const profile = speakerProfileRef.current;
        profile.speakingRateWpm = profile.speakingRateWpm
          ? Math.round(profile.speakingRateWpm * 0.65 + speakingRateWpm * 0.35)
          : speakingRateWpm;
        profile.samples += 1;
      }
      adaptiveTrackerRef.current.setSemanticHint(hint);
      probeTranscriptRef.current = { transcript, speechRevision, hint };
      update({ semanticStatus: hint.status, speakingRateWpm: speakerProfileRef.current.speakingRateWpm || 0 });
      emit('TURN_SEMANTIC_PROBE', 'complete', { speechRevision, transcriptChars: transcript.length, speakingRateWpm: speakerProfileRef.current.speakingRateWpm, ...hint });
    } catch (error) {
      if (controller.signal.aborted) return;
      adaptiveTrackerRef.current.setSemanticHint({ status: 'unknown', confidence: 0, reason: 'probe_failed' });
      emit('TURN_SEMANTIC_PROBE', 'failed', { speechRevision, error: error.message });
    } finally {
      if (probeAbortRef.current === controller) probeAbortRef.current = null;
    }
  }, [apiBase, emit, update]);

  const upload = useCallback(async (blob, durationMs, mimeType, turn) => {
    update({ uploadStatus: 'uploading', sttStatus: 'waiting' });
    emit('UPLOAD_STARTED', 'started', { bytes: blob.size, durationMs, mimeType });
    emit('WAITING_FOR_STT', 'started', { bytes: blob.size, durationMs, mimeType });
    let lastError;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      if (!callbacksRef.current.isTurnCurrent?.(turn.turnId)) throw new Error('Stale recording turn discarded.');
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = window.crypto?.randomUUID?.() || `${Date.now()}-${attempt}`;
      const form = new FormData();
      form.append('file', blob, `voice-${requestId}.${extensionForMime(mimeType)}`);
      form.append('request_id', requestId);
      form.append('duration_ms', String(durationMs));
      form.append('client_mime', mimeType);
      try {
        const response = await fetch(`${apiBase}/ai/transcribe/stream`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${tokenRef.current}` },
          body: form,
          signal: controller.signal,
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw Object.assign(new Error(payload.detail || `Transcription failed with HTTP ${response.status}.`), { response: { status: response.status, data: payload } });
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let transcript = '';
        let sttMetadata = {};
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
          let boundary = buffer.indexOf('\n\n');
          while (boundary >= 0) {
            const block = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            const raw = block.split('\n').filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trim()).join('\n');
            if (raw) {
              const event = JSON.parse(raw);
              if (event.event === 'upload_complete') {
                update({ uploadStatus: 'complete', sttStatus: 'processing' });
                emit('UPLOAD_COMPLETED', 'complete', { attempt, bytes: event.bytes_received, serverElapsedMs: event.server_elapsed_ms });
              } else if (event.event === 'stt_started') {
                emit('STT_STARTED', 'started', { attempt });
              } else if (event.event === 'stt_complete') {
                transcript = event.transcript?.trim() || '';
                sttMetadata = event;
                emit('STT_COMPLETED', 'complete', { attempt, providerDurationMs: event.provider_duration_ms, language: event.language });
              } else if (event.event === 'error') {
                throw new Error(event.error || 'Speech transcription failed.');
              }
            }
            boundary = buffer.indexOf('\n\n');
          }
        }
        update({ uploadStatus: 'complete', sttStatus: 'complete' });
        emit('TRANSCRIPT_RETURNED', 'complete', { attempt, confidence: sttMetadata.confidence, language: sttMetadata.language });
        return transcript;
      } catch (error) {
        lastError = error;
        if (controller.signal.aborted) throw new Error('Audio upload was cancelled.');
        emit('AUDIO_UPLOAD', 'failed', { attempt, httpStatus: error.response?.status, error: error.response?.data?.detail || error.message });
        if (error.response?.status && error.response.status < 500) break;
        if (attempt < 3) await new Promise((resolve) => {
          const timer = setTimeout(resolve, 400 * (2 ** (attempt - 1)));
          timersRef.current.add(timer);
        });
      }
    }
    update({ uploadStatus: 'failed', sttStatus: 'failed' });
    throw new Error(lastError?.response?.data?.detail || lastError?.message || 'Speech transcription failed.');
  }, [apiBase, emit, update]);

  const finalize = useCallback(async (blob, durationMs, mimeType, turn) => {
    emit('BLOB_FINALIZED', 'complete', { bytes: blob.size, durationMs, mimeType });
    update({ blobSize: blob.size, blobDuration: durationMs });
    emit('BLOB_CREATED', blob.size >= 512 ? 'complete' : 'empty', { bytes: blob.size, durationMs, mimeType });
    await releaseResources({ stopRecorder: false });
    if (!callbacksRef.current.isTurnCurrent?.(turn.turnId)) return;
    if (blob.size < 512) throw new Error('The recorder produced an empty audio blob.');
    const tracker = adaptiveTrackerRef.current.getSnapshot();
    const cachedProbe = probeTranscriptRef.current;
    setIsTranscribing(true);
    try {
      if (cachedProbe?.transcript && cachedProbe.speechRevision === tracker.speechRevision) {
        emit('UPLOAD_STARTED', 'reused_probe', { bytes: blob.size, durationMs, mimeType });
        emit('UPLOAD_COMPLETED', 'reused_probe', { bytes: blob.size, semanticStatus: cachedProbe.hint.status });
        emit('STT_STARTED', 'reused_probe', { speechRevision: tracker.speechRevision });
        emit('STT_COMPLETED', 'reused_probe', { speechRevision: tracker.speechRevision, semanticStatus: cachedProbe.hint.status });
        emit('TRANSCRIPT_RETURNED', 'complete', { source: 'semantic_probe', semanticStatus: cachedProbe.hint.status });
        callbacksRef.current.onTranscript?.(cachedProbe.transcript, turn);
        return;
      }
      const transcript = await upload(blob, durationMs, mimeType, turn);
      if (!transcript) throw new Error('No speech was detected in the recording.');
      callbacksRef.current.onTranscript?.(transcript, turn);
    } catch (error) {
      reportError('AUDIO_PROCESSING_FAILED', error);
    } finally {
      setIsTranscribing(false);
    }
  }, [emit, releaseResources, reportError, update, upload]);

  const stopTurn = useCallback((process = true) => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return false;
    processRef.current = process && voiceDetectedRef.current;
    probeAbortRef.current?.abort();
    probeAbortRef.current = null;
    clearTimers();
    emit('RECORDING_STOP_REQUESTED', 'complete', { process: processRef.current });
    try { recorder.requestData?.(); } catch (_) { /* final data arrives on stop */ }
    try { recorder.stop(); emit('RECORDING_STOPPED', 'requested', { process: processRef.current }); return true; }
    catch (error) { reportError('RECORDER_STOP_FAILED', error); return false; }
  }, [clearTimers, emit, reportError]);

  const monitor = useCallback(() => {
    const analyser = analyserRef.current;
    const recorder = recorderRef.current;
    if (!analyser || recorder?.state !== 'recording') return;
    const samples = new Float32Array(analyser.fftSize);
    const spectrum = new Uint8Array(analyser.frequencyBinCount);
    analyser.getFloatTimeDomainData(samples); analyser.getByteFrequencyData(spectrum);
    let sum = 0; let peak = 0;
    samples.forEach((sample) => { sum += sample * sample; peak = Math.max(peak, Math.abs(sample)); });
    const rms = Math.sqrt(sum / samples.length);
    const fftEnergy = spectrum.reduce((total, value) => total + value, 0) / Math.max(1, spectrum.length) / 255;
    const now = performance.now(); const elapsed = now - startedAtRef.current;
    if (rms > 0.0008 || peak > 0.003) micSignalRef.current = true;
    const speaking = rms > 0.009 || peak > 0.03;
    const observation = adaptiveTrackerRef.current.observe({ now, speaking, energy: Math.max(rms, peak) });
    if (speaking) {
      lastVoiceAtRef.current = now;
      if (!voiceDetectedRef.current) {
        voiceDetectedRef.current = true;
        voiceStartedAtRef.current = now;
        emit('VOICE_DETECTED', 'complete', { rms, peak });
        emit('RECORDING_STARTED', 'complete');
      }
      if (observation.resumed) {
        const pauseMs = Math.round(observation.state.resumedPauses.at(-1) || 0);
        pauseHistoryRef.current.push(pauseMs);
        if (pauseHistoryRef.current.length > 8) pauseHistoryRef.current.shift();
        probeAbortRef.current?.abort();
        probeAbortRef.current = null;
        probeTranscriptRef.current = null;
        emit('SPEECH_RESUMED', 'complete', { pauseMs, pauseCount: observation.state.resumedPauses.length, speechRevision: observation.state.speechRevision });
      }
    }
    setAudioLevel(Math.min(100, Math.round(rms * 1400)));
    update({ rms, peak, fftEnergy, audioContextState: contextRef.current?.state || 'none' });
    if (elapsed > 2500 && !micSignalRef.current) { reportError('NO_AUDIO_SIGNAL', 'The microphone track is active but no audio signal is reaching the browser.', { rms, peak }); stopTurn(false); return; }
    const tracker = adaptiveTrackerRef.current.getSnapshot();
    const silenceMs = voiceDetectedRef.current ? now - tracker.lastVoiceAt : 0;
    const resumedPauses = pauseHistoryRef.current.slice(-4);
    const turnDecision = calculateAdaptiveTurnDecision({
      activeSpeechMs: tracker.activeSpeechMs,
      elapsedMs: elapsed,
      silenceMs,
      burstCount: tracker.burstCount,
      resumedPauses,
      recentVoiceEnergy: tracker.recentVoiceEnergy,
      currentEnergy: Math.max(rms, peak),
      semanticHint: tracker.semanticHint,
      probePending: tracker.probePending,
      speakingRateWpm: speakerProfileRef.current.speakingRateWpm,
    });
    update({
      turnConfidence: Number(turnDecision.confidence.toFixed(2)),
      adaptiveSilenceMs: turnDecision.thresholdMs,
      pauseCount: resumedPauses.length,
      semanticStatus: tracker.semanticHint?.status || 'unknown',
      turnDecision: turnDecision.shouldEnd ? 'end' : silenceMs >= turnDecision.candidateAtMs ? 'grace' : 'listening',
    });
    if (voiceDetectedRef.current && silenceMs >= turnDecision.candidateAtMs && !tracker.candidateStartedAt) {
      adaptiveTrackerRef.current.setCandidate(now);
      emit('END_OF_TURN_CANDIDATE', 'started', { silenceMs: Math.round(silenceMs), ...turnDecision });
    }
    if (voiceDetectedRef.current && turnDecision.shouldProbe && lastProbedRevisionRef.current !== tracker.speechRevision) {
      lastProbedRevisionRef.current = tracker.speechRevision;
      try { recorder.requestData?.(); } catch (_) { /* existing frames are sufficient */ }
      const probeBlob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      requestSemanticProbe({ blob: probeBlob, durationMs: Math.round(elapsed), mimeType: recorder.mimeType || 'audio/webm', turn: turnRef.current, speechRevision: tracker.speechRevision });
    }
    if (voiceDetectedRef.current && turnDecision.shouldEnd) {
      emit('SPEECH_ENDED', 'complete', {
        speechEndedAtMs: tracker.lastVoiceAt,
        silenceMs: Math.round(silenceMs),
        thresholdMs: turnDecision.thresholdMs,
        activeSpeechMs: Math.round(tracker.activeSpeechMs),
        confidence: turnDecision.confidence,
        pauseCount: resumedPauses.length,
        semanticStatus: tracker.semanticHint?.status || 'unknown',
        reason: turnDecision.hardMaximumReached ? 'hard_maximum' : 'confidence_threshold',
      });
      emit('VAD_FIRED', 'complete', { thresholdMs: turnDecision.thresholdMs, confidence: turnDecision.confidence });
      stopTurn(true);
      return;
    }
    if (!voiceDetectedRef.current && elapsed > 8000) { reportError('NO_SPEECH_DETECTED', 'Audio is present, but no speech was detected within eight seconds.', { rms, peak }); stopTurn(false); return; }
    frameRef.current = requestAnimationFrame(monitor);
  }, [emit, reportError, requestSemanticProbe, stopTurn, update]);

  const startTurn = useCallback(async (turn) => {
    if (!featuresRef.current.mediaRecorder) { reportError('MEDIA_RECORDER_UNSUPPORTED', 'MediaRecorder is unavailable in this browser.'); return false; }
    if (recorderRef.current?.state === 'recording' || isTranscribing) { emit('DUPLICATE_RECORD_REQUEST', 'ignored'); return false; }
    turnRef.current = turn;
    chunksRef.current = []; processRef.current = false; voiceDetectedRef.current = false; micSignalRef.current = false; voiceStartedAtRef.current = 0;
    adaptiveTrackerRef.current = createAdaptiveTurnTracker();
    lastProbedRevisionRef.current = -1;
    probeTranscriptRef.current = null;
    update({ recoveryAttempts: 0, blobSize: 0, blobDuration: 0, uploadStatus: 'idle', sttStatus: 'idle', lastError: '', lastErrorCode: '', turnConfidence: 0, adaptiveSilenceMs: 0, pauseCount: 0, semanticStatus: 'unknown', turnDecision: 'listening', speakingRateWpm: speakerProfileRef.current.speakingRateWpm || 0 });
    try {
      const stream = await acquireMicrophone(turn);
      if (!stream) return false;
      if (!callbacksRef.current.isTurnCurrent?.(turn.turnId)) { await releaseResources(); return false; }
      const recorder = createRecorder(recorderStreamRef.current);
      recorderRef.current = recorder;
      const mimeType = recorder.mimeType || 'audio/webm';
      startedAtRef.current = performance.now(); lastVoiceAtRef.current = startedAtRef.current;
      recorder.ondataavailable = (event) => { if (event.data.size > 0) chunksRef.current.push(event.data); emit('AUDIO_FRAME', event.data.size > 0 ? 'received' : 'empty', { bytes: event.data.size }); };
      recorder.onerror = (event) => reportError('RECORDER_ERROR', event.error || new Error('MediaRecorder error'));
      recorder.onstop = async () => {
        const durationMs = Math.round(performance.now() - startedAtRef.current);
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const shouldProcess = processRef.current;
        setIsRecording(false); update({ recorderState: 'inactive' });
        if (shouldProcess) await finalize(blob, durationMs, mimeType, turn);
        else await releaseResources({ stopRecorder: false });
      };
      recorder.start(100);
      setIsRecording(true);
      update({ recorderState: 'recording', codec: mimeType, microphoneStatus: 'listening' });
      emit('LISTENING', 'started', { mimeType });
      frameRef.current = requestAnimationFrame(monitor);
      const maxTimer = setTimeout(() => stopTurn(true), 30000); timersRef.current.add(maxTimer);
      return true;
    } catch (error) {
      await releaseResources();
      if (callbacksRef.current.isTurnCurrent?.(turn.turnId) && !diagnostics.lastError) reportError('CAPTURE_START_FAILED', error);
      return false;
    }
  }, [acquireMicrophone, createRecorder, diagnostics.lastError, emit, finalize, isTranscribing, monitor, releaseResources, reportError, stopTurn, update]);

  const cleanup = useCallback(async () => {
    turnRef.current = null;
    processRef.current = false;
    await releaseResources();
  }, [releaseResources]);

  useEffect(() => () => { releaseResources(); }, [releaseResources]);

  return {
    audioLevel, cleanup, diagnostics, environment: environmentRef.current, events, features: featuresRef.current,
    isRecording, isTranscribing, startTurn, stopTurn, supportsCapture: featuresRef.current.getUserMedia && featuresRef.current.mediaRecorder,
  };
};
